/**
 * El hilo de una conversación (§6 y §15.3): los mensajes ordenados por su `clave`, con separadores por
 * día en la zona del negocio y, arriba, «Ver mensajes anteriores» mientras haya más atrás.
 *
 * El scroll propio va SÓLO desde `xl`, donde el hilo está al lado de la lista: se queda con el alto que dejan la
 * ficha y la caja en la columna (`flex-1`), nunca menos de 8rem. Con `max-h-[calc(100vh-22rem)]` medía lo mismo
 * con o sin espacio, y a 1440x900 empujaba la caja fuera de la pantalla. En el celular no tiene alto máximo: el
 * `max-h-[60vh]` de antes era un scroll adentro del scroll de la página.
 *
 * `pie` va al final, adentro del mismo scroll (hoy, la tarjeta de «Reabrir con plantilla»): en la columna de `xl`,
 * como un scroll aparte entre el hilo y la caja, le quedaban 371 px de sus 459 a 1440x900 y «Mandar plantilla»
 * quedaba oculto; acá se lleva a la vista moviendo el hilo.
 *
 * `relative` junto al scroll: sin él, un `sr-only` de adentro (la `legend` de las plantillas) toma de referencia algo
 * de más arriba, se escapa del recorte y le da scroll a la página entera (a 1440x900, 2.802 px de alto).
 */

import type { ReactNode, RefObject } from "react";
import { Loader2 } from "lucide-react";
import type { ItemHilo } from "../../types.conversaciones";
import { agruparPorDia } from "../../logic/conversaciones";
import { Burbuja } from "./Burbuja";

export function Hilo({ items, ahora, cursorAnteriores, cargandoAnteriores, errorAnteriores, onAnteriores, onReabrir, onReintentar, contenedorRef, finRef, pie }: {
  /** Ya ordenados (`ordenarItems`). */
  items: ItemHilo[];
  ahora: Date;
  cursorAnteriores: string | null;
  cargandoAnteriores: boolean;
  errorAnteriores: string;
  onAnteriores: () => void;
  onReabrir: () => void;
  onReintentar: (texto: string) => void;
  contenedorRef?: RefObject<HTMLDivElement>;
  finRef?: RefObject<HTMLDivElement>;
  /** Lo que va después del último mensaje, adentro del scroll del hilo. */
  pie?: ReactNode;
}) {
  const grupos = agruparPorDia(items, ahora);
  return (
    <div ref={contenedorRef} className="space-y-3 xl:relative xl:min-h-[8rem] xl:flex-1 xl:overflow-y-auto" aria-label="Mensajes de la conversación">
      {cursorAnteriores && (
        <div className="text-center">
          <button type="button" onClick={onAnteriores} disabled={cargandoAnteriores}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {cargandoAnteriores && <Loader2 size={12} className="animate-spin" />} Ver mensajes anteriores
          </button>
          {errorAnteriores && <p role="alert" className="mt-1 text-xs text-red-700">{errorAnteriores}</p>}
        </div>
      )}
      {!items.length && <p className="py-6 text-center text-sm text-slate-500">Todavía no hay mensajes para mostrar.</p>}
      {grupos.map((g) => (
        <div key={g.dia} className="space-y-2">
          <div className="flex justify-center">
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-500">{g.titulo}</span>
          </div>
          {g.items.map((i) => <Burbuja key={i.id} item={i} onReabrir={onReabrir} onReintentar={onReintentar} />)}
        </div>
      ))}
      <div ref={finRef} data-testid="fin-del-hilo" />
      {pie}
    </div>
  );
}

export default Hilo;
